import React, { useState, useEffect } from "react";
import {
  FiCheck,
  FiX,
  FiZap,
  FiShare2,
  FiShield,
  FiClock,
  FiAlertTriangle,
  FiActivity,
  FiRefreshCw
} from "react-icons/fi";

export interface CmtsRoutingRuleCustomFormProps {
  initialValues?: Record<string, unknown> | null;
  busy?: boolean;
  error?: string | null;
  submitLabel?: string;
  isEdit?: boolean;
  onCancel: () => void;
  onSubmit: (payload: Record<string, unknown>) => void | Promise<void>;
}

const SOURCE_OPTIONS = ["Customer", "Internal", "Supplier", "Safety", "Audit", "Warranty"];
const CATEGORY_OPTIONS = [
  "Product Quality",
  "Dimensional Non-conformance",
  "Surface & Visual Defects",
  "Functional Failure",
  "Incoming Raw Material",
  "Packaging & Labeling",
  "Delivery & Logistics"
];
const PRIORITY_OPTIONS = [
  { label: "Critical", val: "Critical", color: "bg-red-50 text-red-700 border-red-200" },
  { label: "High", val: "High", color: "bg-orange-50 text-orange-700 border-orange-200" },
  { label: "Medium", val: "Medium", color: "bg-blue-50 text-blue-700 border-blue-200" },
  { label: "Low", val: "Low", color: "bg-slate-50 text-slate-700 border-slate-200" }
];
const TARGET_QUEUES = [
  "Quality Triage Queue",
  "Plant QA Triage",
  "Customer Quality Desk",
  "Supplier Quality Desk",
  "Executive Escalation Desk",
  "Fast-Track Containment Team"
];
const DEPARTMENTS = [
  "Quality Assurance",
  "Manufacturing Plant 1",
  "Assembly Line 2",
  "Material Handling & Receiving",
  "Engineering & Design",
  "Customer Service"
];
const OWNERS = [
  "Kumar (QA Lead)",
  "John Smith (Lead Quality Analyst)",
  "Robert Vance (Supplier Quality Eng.)",
  "Sarah Jenkins (Plant QA Manager)",
  "Platform System Auto-Assign"
];
const SLA_POLICIES = [
  "Critical 24H SLA",
  "Rapid 48H SLA",
  "Standard 7-Day SLA",
  "Low Priority 14-Day SLA",
  "Supplier 5-Day SLA"
];
const ESCALATION_POLICIES = [
  "Executive 2-Tier Escalation",
  "Plant Manager 24H Escalation",
  "Standard 3-Tier Quality Escalation",
  "Supplier Quality Management Escalation"
];
const WORKFLOW_PROFILES = [
  "Standard 8D Investigation Workflow",
  "Quick Containment Workflow",
  "Supplier 5-Why Investigation",
  "Fast-Track RCA & Closure"
];

export function CmtsRoutingRuleCustomForm({
  initialValues,
  busy = false,
  error = null,
  submitLabel,
  isEdit = false,
  onCancel,
  onSubmit
}: CmtsRoutingRuleCustomFormProps) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [sourceType, setSourceType] = useState("Customer");
  const [category, setCategory] = useState("Product Quality");
  const [priority, setPriority] = useState("High");
  const [targetQueue, setTargetQueue] = useState("Quality Triage Queue");
  const [department, setDepartment] = useState("Quality Assurance");
  const [owner, setOwner] = useState("Kumar (QA Lead)");
  const [slaPolicy, setSlaPolicy] = useState("Standard 7-Day SLA");
  const [escalationPolicy, setEscalationPolicy] = useState("Executive 2-Tier Escalation");
  const [workflowProfile, setWorkflowProfile] = useState("Standard 8D Investigation Workflow");
  const [customCondition, setCustomCondition] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [priorityOrder, setPriorityOrder] = useState(10);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (initialValues) {
      setCode(String(initialValues.code ?? ""));
      setName(String(initialValues.ruleName ?? initialValues.name ?? ""));
      setSourceType(String(initialValues.sourceType ?? "Customer"));
      setCategory(String(initialValues.category ?? "Product Quality"));
      setPriority(String(initialValues.priority ?? "High"));
      setTargetQueue(String(initialValues.targetQueue ?? "Quality Triage Queue"));
      setDepartment(String(initialValues.department ?? initialValues.departmentId ?? "Quality Assurance"));
      setOwner(String(initialValues.owner ?? initialValues.ownerId ?? "Kumar (QA Lead)"));
      setSlaPolicy(String(initialValues.slaPolicy ?? "Standard 7-Day SLA"));
      setEscalationPolicy(String(initialValues.escalationPolicy ?? "Executive 2-Tier Escalation"));
      setWorkflowProfile(String(initialValues.workflowProfile ?? "Standard 8D Investigation Workflow"));
      setCustomCondition(String(initialValues.condition ?? initialValues.description ?? ""));
      setIsActive(initialValues.active !== false && initialValues.status !== "Inactive");
      setPriorityOrder(Number(initialValues.priorityOrder ?? initialValues.sortOrder ?? 10));
    } else {
      setCode("RR-ROUTING-010");
      setName("New Complaint Routing Rule");
      setSourceType("Customer");
      setCategory("Product Quality");
      setPriority("High");
      setTargetQueue("Quality Triage Queue");
      setDepartment("Quality Assurance");
      setOwner("Kumar (QA Lead)");
      setSlaPolicy("Standard 7-Day SLA");
      setEscalationPolicy("Executive 2-Tier Escalation");
      setWorkflowProfile("Standard 8D Investigation Workflow");
      setCustomCondition("");
      setIsActive(true);
      setPriorityOrder(100);
    }
  }, [initialValues]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim() || !name.trim()) {
      setLocalError("Rule Code and Rule Name are required.");
      return;
    }
    setLocalError(null);

    const generatedCondition = customCondition.trim()
      ? customCondition.trim()
      : `Source = ${sourceType} AND Category = ${category} AND Priority = ${priority}`;

    const payload: Record<string, unknown> = {
      ...(initialValues ?? {}),
      id: initialValues?.id,
      code: code.trim().toUpperCase(),
      name: name.trim(),
      ruleName: name.trim(),
      description: generatedCondition,
      condition: generatedCondition,
      sourceType,
      category,
      priority,
      targetQueue,
      department,
      departmentId: department,
      owner,
      ownerId: owner,
      slaPolicy,
      escalationPolicy,
      workflowProfile,
      priorityOrder,
      sortOrder: priorityOrder,
      active: isActive,
      status: isActive ? "Active" : "Inactive",
      masterType: "routing",
      group: "routing"
    };

    await onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 text-xs text-slate-800">
      {/* Top Banner Info */}
      <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 p-4 rounded-2xl text-white flex items-center justify-between border border-indigo-900/40 shadow-md">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-600/30 border border-indigo-400/40 flex items-center justify-center text-indigo-300 text-lg shadow-inner">
            <FiShare2 />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-extrabold text-sm tracking-tight text-white">
                {isEdit ? "Edit Routing Rule" : "Create Routing Rule"}
              </h3>
              <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-indigo-500/30 text-indigo-200 border border-indigo-400/30">
                {code || "RR-NEW"}
              </span>
            </div>
            <p className="text-[11px] text-slate-300 mt-0.5">
              Define matching trigger conditions, target queue destination, ownership and SLA policies.
            </p>
          </div>
        </div>
      </div>

      {(error || localError) && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 font-semibold text-xs flex items-center gap-2">
          <FiAlertTriangle className="text-sm flex-shrink-0" />
          <span>{error || localError}</span>
        </div>
      )}

      {/* Section 1: Rule Identity & Matching Conditions */}
      <div className="bg-slate-50/80 rounded-2xl p-4 border border-slate-200/80 space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-slate-200">
          <div className="w-6 h-6 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-xs">
            1
          </div>
          <h4 className="font-extrabold text-slate-900 text-sm">
            Rule Identification & Matching Conditions
          </h4>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block font-bold text-slate-700 mb-1">
              Rule Code <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              required
              placeholder="e.g. RR-ROUTING-010"
              className="w-full py-2 px-3 rounded-xl border border-slate-300 bg-white text-slate-900 font-mono font-bold focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block font-bold text-slate-700 mb-1">
              Rule Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              placeholder="e.g. Critical Customer Complaint Routing"
              className="w-full py-2 px-3 rounded-xl border border-slate-300 bg-white text-slate-900 font-semibold focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-1">
          <div>
            <label className="block font-bold text-slate-700 mb-1">Source Type</label>
            <select
              value={sourceType}
              onChange={(e) => setSourceType(e.target.value)}
              className="w-full py-2 px-3 rounded-xl border border-slate-300 bg-white text-slate-900 font-semibold focus:ring-2 focus:ring-indigo-500"
            >
              {SOURCE_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-bold text-slate-700 mb-1">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full py-2 px-3 rounded-xl border border-slate-300 bg-white text-slate-900 font-semibold focus:ring-2 focus:ring-indigo-500"
            >
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-bold text-slate-700 mb-1">Priority / Severity Level</label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              className="w-full py-2 px-3 rounded-xl border border-slate-300 bg-white text-slate-900 font-bold focus:ring-2 focus:ring-indigo-500"
            >
              {PRIORITY_OPTIONS.map((p) => (
                <option key={p.val} value={p.val}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Section 2: Target Destination & Ownership */}
      <div className="bg-slate-50/80 rounded-2xl p-4 border border-slate-200/80 space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-slate-200">
          <div className="w-6 h-6 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-xs">
            2
          </div>
          <h4 className="font-extrabold text-slate-900 text-sm">
            Target Destination, Queue & Ownership
          </h4>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block font-bold text-slate-700 mb-1">
              Target Queue <span className="text-red-500">*</span>
            </label>
            <select
              value={targetQueue}
              onChange={(e) => setTargetQueue(e.target.value)}
              className="w-full py-2 px-3 rounded-xl border border-slate-300 bg-white text-slate-900 font-bold focus:ring-2 focus:ring-indigo-500"
            >
              {TARGET_QUEUES.map((q) => (
                <option key={q} value={q}>
                  {q}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-bold text-slate-700 mb-1">Target Department</label>
            <select
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              className="w-full py-2 px-3 rounded-xl border border-slate-300 bg-white text-slate-900 font-semibold focus:ring-2 focus:ring-indigo-500"
            >
              {DEPARTMENTS.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-bold text-slate-700 mb-1">Owner / Assignee</label>
            <select
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
              className="w-full py-2 px-3 rounded-xl border border-slate-300 bg-white text-slate-900 font-semibold focus:ring-2 focus:ring-indigo-500"
            >
              {OWNERS.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Section 3: SLA & Workflow Governance */}
      <div className="bg-slate-50/80 rounded-2xl p-4 border border-slate-200/80 space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-slate-200">
          <div className="w-6 h-6 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-xs">
            3
          </div>
          <h4 className="font-extrabold text-slate-900 text-sm">
            SLA Policy, Escalation & Workflow Profiles
          </h4>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block font-bold text-slate-700 mb-1">
              SLA Policy <span className="text-red-500">*</span>
            </label>
            <select
              value={slaPolicy}
              onChange={(e) => setSlaPolicy(e.target.value)}
              className="w-full py-2 px-3 rounded-xl border border-slate-300 bg-white text-slate-900 font-semibold focus:ring-2 focus:ring-indigo-500"
            >
              {SLA_POLICIES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-bold text-slate-700 mb-1">Escalation Policy</label>
            <select
              value={escalationPolicy}
              onChange={(e) => setEscalationPolicy(e.target.value)}
              className="w-full py-2 px-3 rounded-xl border border-slate-300 bg-white text-slate-900 font-semibold focus:ring-2 focus:ring-indigo-500"
            >
              {ESCALATION_POLICIES.map((ep) => (
                <option key={ep} value={ep}>
                  {ep}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-bold text-slate-700 mb-1">
              Workflow Profile <span className="text-red-500">*</span>
            </label>
            <select
              value={workflowProfile}
              onChange={(e) => setWorkflowProfile(e.target.value)}
              className="w-full py-2 px-3 rounded-xl border border-slate-300 bg-white text-slate-900 font-semibold focus:ring-2 focus:ring-indigo-500"
            >
              {WORKFLOW_PROFILES.map((w) => (
                <option key={w} value={w}>
                  {w}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Section 4: Live Evaluated Routing Logic Expression Card */}
      <div className="bg-indigo-950 text-indigo-100 rounded-2xl p-4 border border-indigo-800 shadow-md space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 font-bold text-white text-xs">
            <FiZap className="text-amber-400" />
            <span>Live Evaluated Routing Logic Expression</span>
          </div>
          <span className="text-[10px] px-2 py-0.5 rounded bg-indigo-900 border border-indigo-700 font-mono text-indigo-300">
            Priority Order: {priorityOrder}
          </span>
        </div>

        <div className="p-3 bg-slate-900/90 rounded-xl font-mono text-xs text-emerald-300 border border-slate-800 space-y-1">
          <p>
            <span className="text-purple-400 font-bold">IF</span> Source = &quot;{sourceType}&quot;{" "}
            <span className="text-purple-400 font-bold">AND</span> Category = &quot;{category}&quot;{" "}
            <span className="text-purple-400 font-bold">AND</span> Priority = &quot;{priority}&quot;
          </p>
          <p className="text-amber-300 font-bold pl-4">
            ➜ ROUTE TO &quot;{targetQueue}&quot; ({department})
          </p>
          <p className="text-sky-300 pl-4">
            ➜ APPLY SLA: [{slaPolicy}] &bull; ESCALATION: [{escalationPolicy}] &bull; WORKFLOW: [{workflowProfile}]
          </p>
        </div>

        <div className="flex items-center justify-between pt-1">
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer font-bold text-slate-200">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-slate-700 bg-slate-800"
              />
              <span>Active Rule</span>
            </label>
          </div>

          <div className="flex items-center gap-2 text-slate-300">
            <span>Priority Order:</span>
            <input
              type="number"
              value={priorityOrder}
              onChange={(e) => setPriorityOrder(Number(e.target.value))}
              className="w-16 py-1 px-2 rounded-lg bg-slate-900 border border-slate-700 text-white font-mono text-center font-bold"
            />
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-200">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="px-5 py-2.5 rounded-xl border border-slate-300 bg-white hover:bg-slate-100 text-slate-700 font-bold text-xs transition"
        >
          Cancel
        </button>

        <button
          type="submit"
          disabled={busy}
          className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white font-bold text-xs shadow-lg shadow-indigo-500/25 flex items-center gap-2 transition disabled:opacity-50"
        >
          {busy ? (
            <>
              <FiRefreshCw className="animate-spin" />
              <span>Saving to Database...</span>
            </>
          ) : (
            <>
              <FiCheck />
              <span>{submitLabel ?? (isEdit ? "Save changes" : "Create & Activate Rule")}</span>
            </>
          )}
        </button>
      </div>
    </form>
  );
}
